import { useState, useEffect, useRef } from 'react';
import { stripMarkup } from '../../utils/narration';
import { useTranslation } from 'react-i18next';
import { useGameStore } from '../../store/gameStore';
import { useAnnounce } from '../../hooks/useAnnounce';
import Dialog from '../ui/Dialog';
import { useWebSocket } from '../../hooks/useWebSocket';
import { gameAPI } from '../../services/api';
import TurnPanel from './TurnPanel';
import TurnBook from './TurnBook';
import BackgroundMusic from '../media/BackgroundMusic';
import AvatarManager from '../avatars/AvatarManager';
import { getApiUrl } from '../../utils/api-url';
import { itemImageUrl, abilityImageUrl } from '../../utils/itemImage';
import ShareLink from './ShareLink';
import { useIsMobile } from '../../hooks/useIsMobile';

export default function GameBoard() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { currentGame, players, setCurrentGame, setPlayers } = useGameStore();
  const [currentPlayer, setCurrentPlayer] = useState<any>(null);
  const announce = useAnnounce();
  const [turns, setTurns] = useState<any[]>([]);
  const [gameState, setGameState] = useState<any>(null);
  const [introScene, setIntroScene] = useState<any>(null);
  const [onlinePlayers, setOnlinePlayers] = useState<string[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [currentLocationMusic, setCurrentLocationMusic] = useState<string | null>(null);
  const [currentLocationName, setCurrentLocationName] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  // Lightbox for reviewing a character's pictures (uploaded photos + the generated portrait).
  const [gallery, setGallery] = useState<{ urls: string[]; i: number; name: string } | null>(null);
  // Detail viewer for items / abilities: image + name + description, arrows step through the list.
  const [detail, setDetail] = useState<{ list: { name: string; desc?: string; url: string }[]; i: number; title: string } | null>(null);
  const [isOpening, setIsOpening] = useState(false);
  const openingRef = useRef(false);
  // Mount the flip-book only once BOTH the intro and the turn history have loaded, so react-pageflip
  // initialises once with the full page set instead of re-initialising as pages trickle in
  // (which left pages unpainted and mis-positioned the book after a reload).
  const [turnsLoaded, setTurnsLoaded] = useState(false);
  const [introLoaded, setIntroLoaded] = useState(false);

  const apiUrl = getApiUrl();

  // Get current player from URL or local storage
  const getCurrentPlayerId = () => {
    const params = new URLSearchParams(window.location.search);
    const urlPlayerId = params.get('playerId');
    if (urlPlayerId) {
      localStorage.setItem(`game_${currentGame?.id}_player`, urlPlayerId);
      return urlPlayerId;
    }
    return localStorage.getItem(`game_${currentGame?.id}_player`) || null;
  };

  const playerId = getCurrentPlayerId();

  // Always load the game + players fresh from the server by the id in the URL, so the
  // board works on direct navigation / resume even if the store is empty or stale.
  // This is what makes the character sheet, inventory, players list, etc. show up.
  useEffect(() => {
    const urlGameId = window.location.pathname.split('/')[2];
    if (!urlGameId) return;
    gameAPI
      .getGame(urlGameId)
      .then((game) => {
        setCurrentGame(game);
        if (Array.isArray(game.players)) setPlayers(game.players);
      })
      .catch((err) => console.error('Failed to load game:', err));
    // once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WebSocket connection
  const { isConnected, onlinePlayers: wsOnlinePlayers, socket } = useWebSocket({
    gameId: currentGame?.id || window.location.pathname.split('/')[2],
    playerId: playerId || undefined,
    onTurnCompleted: (data) => {
      setGameState(data.gameState);
      // The server advances the turn order and sends the new current player with every
      // completed turn. Without picking it up here isMyTurn/turnStatus stay frozen at
      // page-load values: the next player never sees the input form and the screen
      // reader never announces their turn until a reload.
      setCurrentPlayer(data.gameState?.currentPlayer ?? null);
      setTurns((prev) => [data.turn, ...prev]);
    },
    // Scene image / narration arrive after the turn text — patch them onto the turn.
    onTurnMedia: (data) => {
      setTurns((prev) => prev.map((t) => {
        const id = t.turnId || t.id;
        if (id !== data.turnId) return t;
        return {
          ...t,
          ...(data.sceneImageId ? { scene_image_id: data.sceneImageId } : {}),
          ...(data.narrationAudioId ? { narration_audio_id: data.narrationAudioId } : {}),
          // The server says no audio is coming — either this language has no offline voice, or
          // synthesis gave up. Without this flag the UI shows "generating…" forever and a screen
          // reader announces a wait that never ends.
          ...(data.narrationUnavailable ? { narration_unavailable: true } : {}),
        };
      }));
    },
    onPlayerOnline: (data) => {
      console.log('Player online:', data);
    },
    onPlayerOffline: (data) => {
      console.log('Player offline:', data);
    },
  });

  // Listen for location music generation updates
  useEffect(() => {
    if (!socket) return;

    const handleMusicGenerated = (data: any) => {
      console.log('Background music generated for location:', data.locationName);

      // If this is the current location, update music
      if (data.locationName === currentLocationName) {
        setCurrentLocationMusic(`${apiUrl}/api/media/${data.musicAssetId}`);
      }

      // Update locations list
      setLocations(prev =>
        prev.map(loc =>
          loc.id === data.locationId
            ? { ...loc, background_music_id: data.musicAssetId }
            : loc
        )
      );
    };

    // Music follows the scene's mood — switch the background track when a new scene sets one.
    const handleSceneMusic = (data: any) => {
      if (data?.musicId) {
        console.log('Scene music →', data.mood);
        setCurrentLocationMusic(`${apiUrl}/api/media/${data.musicId}`);
      }
    };

    // A player joined (or their generated portrait finished) — upsert their card live,
    // so everyone already in the game sees it without an F5.
    const upsertPlayer = (data: any) => {
      const incoming = data?.player;
      if (!incoming?.id) return;
      const current = useGameStore.getState().players;
      const idx = current.findIndex((p) => p.id === incoming.id);
      if (idx === -1) {
        setPlayers([...current, incoming]);
      } else {
        const next = current.slice();
        next[idx] = { ...next[idx], ...incoming };
        setPlayers(next);
      }
    };

    socket.on('location:music:generated', handleMusicGenerated);
    socket.on('scene:music', handleSceneMusic);
    socket.on('player:joined', upsertPlayer);
    socket.on('player:updated', upsertPlayer);

    return () => {
      socket.off('location:music:generated', handleMusicGenerated);
      socket.off('scene:music', handleSceneMusic);
      socket.off('player:joined', upsertPlayer);
      socket.off('player:updated', upsertPlayer);
    };
  }, [socket, currentLocationName, apiUrl]);

  useEffect(() => {
    setOnlinePlayers(wsOnlinePlayers.map(p => p.id));
  }, [wsOnlinePlayers]);

  // Load initial game state
  useEffect(() => {
    if (!currentGame) return;

    const loadGameState = async () => {
      try {
        const state = await gameAPI.getGameState(currentGame.id);
        setGameState(state.gameState);
        setCurrentPlayer(state.gameState.currentPlayer);

        const history = await gameAPI.getTurnHistory(currentGame.id);
        setTurns(history.turns);
        setTurnsLoaded(true);

        // Fresh game (intro shown, no turns yet) → have the GM open the first playable scene.
        // The book then flips to it (arrives via the turn:completed socket event). Idempotent.
        if ((history.turns?.length || 0) === 0 && currentGame.status === 'active' && !openingRef.current) {
          openingRef.current = true;
          setIsOpening(true);
          gameAPI.generateOpeningScene(currentGame.id)
            .catch((e) => console.error('Opening scene failed:', e))
            .finally(() => setIsOpening(false));
        }
      } catch (error) {
        console.error('Failed to load game state:', error);
        setTurnsLoaded(true); // don't block the book forever on a transient error
      }
    };

    loadGameState();
  }, [currentGame]);

  // Load the intro so we can show it as the opening scene before the first action.
  useEffect(() => {
    const id = window.location.pathname.split('/')[2];
    if (!id) { setIntroLoaded(true); return; }
    fetch(`${apiUrl}/api/games/${id}/intro`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setIntroScene(data))
      .catch(() => {})
      .finally(() => setIntroLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load locations and set background music
  useEffect(() => {
    if (!currentGame) return;

    const loadLocations = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/games/${currentGame.id}/world/map`);
        const data = await response.json();
        setLocations(data.locations);

        // Find first location with background music
        const locationWithMusic = data.locations.find((loc: any) => loc.background_music_id);
        if (locationWithMusic) {
          setCurrentLocationMusic(`${apiUrl}/api/media/${locationWithMusic.background_music_id}`);
          setCurrentLocationName(locationWithMusic.name);
        }
      } catch (error) {
        console.error('Failed to load locations:', error);
      }
    };

    loadLocations();
  }, [currentGame, apiUrl]);

  // NB: everything below is computed before the `!currentGame` bail-out on purpose. The two
  // announcement effects are hooks, and hooks after a conditional return change in number
  // between renders — React throws "rendered fewer hooks than expected" the moment the game
  // finishes loading.
  // Resolve "my" player — fall back to the only player in a single-player game.
  const myPlayer = players.find(p => p.id === playerId) || (players.length === 1 ? players[0] : undefined);
  // If this browser isn't bound to a specific player (the host/table view that created the game
  // and didn't "join" as a character), let it drive whoever's turn it currently is. Otherwise a
  // game with remote joiners stalls forever on a player who has no device open here.
  const effectivePlayerId = playerId || myPlayer?.id || currentPlayer?.id || '';
  // In a solo game it's always your turn; otherwise it's the current player's.
  const isMyTurn = players.length <= 1 ? true : currentPlayer?.id === effectivePlayerId;
  // Co-located scene: if another player shares my location, everyone present declares an
  // action simultaneously (the GM gathers all prompts, then resolves one shared scene) —
  // so let me act regardless of whose formal turn it is.
  const myLocation = myPlayer?.current_location_id ?? null;
  // Everyone standing on my scene (including me) — they declare together in one combined form.
  const presentPlayers = (myLocation
    ? players.filter((p) => (p.current_location_id ?? null) === myLocation)
    : myPlayer ? [myPlayer] : []
  ).map((p) => ({ id: p.id, name: p.name }));
  const coLocated = presentPlayers.length > 1;
  const canAct = isMyTurn || coLocated;

  // Accessibility: a blind player hears every new scene and knows when to act. The announcing
  // itself is done by the app-wide live regions via useAnnounce — this screen used to own two
  // regions of its own, which competed with the two in TurnBook and read interleaved.
  const latestNarrative = stripMarkup(
    turns[0]?.action_result || (turns[0] as any)?.actionResult || introScene?.narrative || ''
  );
  const turnStatus = canAct
    ? (coLocated ? t('board.srSharedScene') : t('board.srYourTurn'))
    : t('board.srPlayerTurn', { name: currentPlayer?.name || t('board.otherPlayer') });

  // Announce the scene first and the turn prompt after it: the prompt is assertive and would
  // otherwise cut off the opening of the story the player just waited for.
  useEffect(() => {
    if (latestNarrative) announce(latestNarrative, 'polite');
  }, [latestNarrative, announce]);

  useEffect(() => {
    if (turnStatus) announce(turnStatus, 'assertive');
  }, [turnStatus, announce]);

  if (!currentGame) {
    return (
      <div style={{
        maxWidth: '800px',
        margin: '4rem auto',
        textAlign: 'center',
        color: '#a0aec0',
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⏳</div>
        <p>{t('board.loadingGame')}</p>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      paddingBottom: '4rem',
    }}>
      {/* Game Header */}
      <div style={{
        background: 'rgba(15, 12, 41, 0.8)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
        padding: isMobile ? '1rem' : '1.5rem 2rem',
        marginBottom: '2rem',
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: isMobile ? '0.75rem' : '1rem', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{
              fontSize: isMobile ? '1.5rem' : '2rem',
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              margin: 0,
            }}>
              {currentGame.name}
            </h1>
            <p style={{ margin: '0.5rem 0 0 0', color: '#a0aec0', fontSize: '0.9rem' }}>
              {currentGame.universe} • {t('board.turnLabel', { count: gameState?.game?.current_turn_number || 0 })}
            </p>
          </div>

          <div style={{ display: 'flex', gap: isMobile ? '0.5rem' : '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Connection Status */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: isConnected ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              borderRadius: '20px',
              border: `1px solid ${isConnected ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: isConnected ? '#10b981' : '#ef4444',
              }} />
              <span style={{ fontSize: '0.85rem', color: isConnected ? '#10b981' : '#ef4444' }}>
                {isConnected ? t('common.online') : t('common.offline')}
              </span>
            </div>

            {/* Exit to menu — placed after the online status; your games are saved. */}
            <button
              onClick={() => { window.location.href = '/games'; }}
              title={t('board.exitTitle')}
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(99, 102, 241, 0.15)',
                border: '1px solid rgba(99, 102, 241, 0.35)',
                borderRadius: '20px',
                color: '#c7d2fe',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(99, 102, 241, 0.28)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(99, 102, 241, 0.15)'; }}
            >
              🏠 {t('nav.myGames')}
            </button>

            {/* Invite players — link to send to remote players (multiplayer games only) */}
            {currentGame.share_code && (currentGame.max_players || 1) > 1 && (
              <button
                onClick={() => setShowInvite(true)}
                title={t('board.inviteTitle')}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(16, 185, 129, 0.12)',
                  border: '1px solid rgba(16, 185, 129, 0.35)',
                  borderRadius: '20px',
                  color: '#6ee7b7',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(16, 185, 129, 0.25)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(16, 185, 129, 0.12)'; }}
              >
                🔗 {t('board.invite')}
              </button>
            )}

            {/* My Player */}
            {myPlayer && (
              <div style={{
                padding: '0.5rem 1rem',
                background: 'rgba(99, 102, 241, 0.1)',
                borderRadius: '20px',
                border: '1px solid rgba(99, 102, 241, 0.3)',
              }}>
                <span style={{ fontSize: '0.85rem', color: '#cbd5e0' }}>
                  {t('board.you')} <strong>{myPlayer.name}</strong>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invite modal — the share link the creator sends to other players */}
      {showInvite && currentGame.share_code && (
        <Dialog title={t('board.invite')} onClose={() => setShowInvite(false)} maxWidth="520px">
          <ShareLink shareCode={currentGame.share_code} />
          <button
            type="button"
            onClick={() => setShowInvite(false)}
            style={{
              width: '100%', padding: '0.85rem', borderRadius: '12px', fontSize: '1rem', fontWeight: 600,
              border: '1px solid rgba(99,102,241,0.35)', background: 'rgba(99,102,241,0.12)',
              color: '#c7d2fe', cursor: 'pointer',
            }}
          >
            {t('npc.close')}
          </button>
        </Dialog>
      )}

      {/* Character picture viewer — flip through every photo the player chose + the AI portrait.
          The image used to carry alt="" inside an unlabelled overlay, so the dialog was empty
          for a screen reader: nothing named it and nothing described its only content. */}
      {gallery && gallery.urls.length > 0 && (
        <Dialog
          title={`${t('board.viewPhotos')} — ${gallery.i + 1} / ${gallery.urls.length}`}
          onClose={() => setGallery(null)}
          showTitle={false}
          bare
          maxWidth="100%"
        >
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '82vh' }}>
            <button
              type="button"
              onClick={() => setGallery(null)}
              aria-label={t('npc.close')}
              style={{
                position: 'absolute', top: '1rem', right: '1rem', zIndex: 2, width: '44px', height: '44px',
                borderRadius: '50%', border: '1px solid rgba(255,255,255,0.25)',
                background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '1.3rem', cursor: 'pointer',
              }}
            >
              <span aria-hidden="true">✕</span>
            </button>

            {gallery.urls.length > 1 && (
              <button
                type="button"
                onClick={() => setGallery((g) => g ? { ...g, i: (g.i - 1 + g.urls.length) % g.urls.length } : g)}
                aria-label={t('home.prevWorld')}
                style={{
                  position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', zIndex: 2,
                  width: '44px', height: '44px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.25)',
                  background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '1.5rem', cursor: 'pointer',
                }}
              >
                <span aria-hidden="true">‹</span>
              </button>
            )}

            <img
              src={gallery.urls[gallery.i]}
              alt={t('board.portraitAlt', { name: gallery.name })}
              style={{ maxWidth: '92vw', maxHeight: '82vh', borderRadius: '16px', objectFit: 'contain', boxShadow: '0 25px 70px rgba(0,0,0,0.7)' }}
            />

            {gallery.urls.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setGallery((g) => g ? { ...g, i: (g.i + 1) % g.urls.length } : g)}
                  aria-label={t('home.nextWorld')}
                  style={{
                    position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', zIndex: 2,
                    width: '44px', height: '44px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.25)',
                    background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '1.5rem', cursor: 'pointer',
                  }}
                >
                  <span aria-hidden="true">›</span>
                </button>
                <div aria-hidden="true" style={{
                  position: 'absolute', bottom: '1.25rem', left: '50%', transform: 'translateX(-50%)',
                  color: '#e2e8f0', fontSize: '0.85rem', background: 'rgba(0,0,0,0.5)', padding: '0.25rem 0.7rem', borderRadius: '10px',
                }}>
                  {gallery.i + 1} / {gallery.urls.length}
                </div>
              </>
            )}
          </div>
        </Dialog>
      )}

      {/* Item / ability detail — big picture, name, description; arrows step through the list */}
      {detail && detail.list.length > 0 && (() => {
        const cur = detail.list[detail.i];
        const step = (d: number) => setDetail((s) => s ? { ...s, i: (s.i + d + s.list.length) % s.list.length } : s);
        return (
          <Dialog
            title={`${detail.title} · ${detail.i + 1}/${detail.list.length}`}
            onClose={() => setDetail(null)}
            showTitle={false}
            maxWidth="360px"
            padding="1.25rem"
          >
            <div style={{ textAlign: 'center' }}>
              <div aria-hidden="true" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#a0aec0', marginBottom: '0.75rem' }}>
                {detail.title} · {detail.i + 1}/{detail.list.length}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                {detail.list.length > 1 && (
                  <button type="button" onClick={() => step(-1)} aria-label={t('home.prevWorld')} style={{
                    width: '40px', height: '40px', flexShrink: 0, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: '1.4rem', cursor: 'pointer',
                  }}><span aria-hidden="true">‹</span></button>
                )}
                {/* The name and description are right below as real text, so the picture itself
                    is decorative — captioning it here would just read everything twice. */}
                <img src={cur.url} alt="" style={{
                  width: 180, height: 180, objectFit: 'cover', borderRadius: '16px',
                  border: '1px solid rgba(99,102,241,0.3)', background: 'rgba(0,0,0,0.35)',
                }} />
                {detail.list.length > 1 && (
                  <button type="button" onClick={() => step(1)} aria-label={t('home.nextWorld')} style={{
                    width: '40px', height: '40px', flexShrink: 0, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: '1.4rem', cursor: 'pointer',
                  }}><span aria-hidden="true">›</span></button>
                )}
              </div>
              <h3 style={{ margin: '1rem 0 0.4rem', color: '#f7fafc', fontSize: '1.15rem' }}>{cur.name}</h3>
              {cur.desc && <p style={{ margin: 0, color: '#cbd5e0', fontSize: '0.9rem', lineHeight: 1.5 }}>{cur.desc}</p>}
              <button type="button" onClick={() => setDetail(null)} style={{
                marginTop: '1.25rem', width: '100%', padding: '0.7rem', borderRadius: '12px',
                border: '1px solid rgba(99,102,241,0.35)', background: 'rgba(99,102,241,0.12)', color: '#c7d2fe', cursor: 'pointer',
              }}>
                {t('npc.close')}
              </button>
            </div>
          </Dialog>
        );
      })()}

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '0 0.75rem' : '0 2rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {/* The book gets the full window width; the character sheet drops below it. */}
            <div>
              {/* The adventure book — written as you play: each turn is a spread
                  (illustration + text), flip back/forward through the pages. */}
              <TurnBook turns={turns} intro={introScene} ready={turnsLoaded && introLoaded} />

              {/* The GM is writing the opening scene right after the intro; the book will flip to it. */}
              {isOpening && turns.length === 0 && (
                <div role="status" style={{
                  background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.35)',
                  borderRadius: '12px', padding: '0.9rem 1.1rem', margin: '0 0 0.5rem', color: '#a7f3d0',
                  fontSize: '0.95rem', lineHeight: 1.5,
                }}>
                  🎬 {t('board.gmWritingScene')}
                </div>
              )}

              <TurnPanel
                gameId={currentGame.id}
                playerId={effectivePlayerId}
                playerName={myPlayer?.name || currentPlayer?.name || t('board.gm')}
                currentPlayer={currentPlayer}
                isMyTurn={canAct}
                sceneMode={coLocated}
                presentPlayers={presentPlayers}
              />
            </div>

          {/* Right Column - Player List & Chat */}
          <div>
            {/* Characters — a compact card per player (several people can play in one browser) */}
            {players.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: '0.7rem', marginBottom: '1.5rem' }}>
              {players.map((mp) => (
              <div key={mp.id} style={{
                background: 'rgba(15, 12, 41, 0.6)',
                backdropFilter: 'blur(20px)',
                borderRadius: '16px',
                border: `1px solid ${mp.id === effectivePlayerId ? 'rgba(16,185,129,0.4)' : 'rgba(99, 102, 241, 0.2)'}`,
                padding: '0.6rem 0.7rem',
              }}>
                <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                  {/* Portrait: the player's OWN uploaded photo wins over the AI-generated one; a
                      letter until anything is ready. Tap to review every picture for this character. */}
                  {(() => {
                    const ad: any = (mp as any).avatar_data || {};
                    const photoUrls: string[] = Array.isArray(ad.photos) ? ad.photos.map((p: string) => `${apiUrl}/media/${p}`) : [];
                    const portraitUrl = ad.portraitId ? `${apiUrl}/api/media/${ad.portraitId}` : null;
                    // Show the user's photo first; the generated portrait is only a fallback.
                    const galleryUrls = [...photoUrls, ...(portraitUrl ? [portraitUrl] : [])];
                    const primaryUrl = photoUrls[0] || portraitUrl || null;
                    const openGallery = galleryUrls.length > 0 ? () => setGallery({ urls: galleryUrls, i: 0, name: mp.name }) : undefined;
                    return (
                      // A plain div with onClick is invisible to the keyboard and announces
                      // nothing; when there are photos this has to be a real button.
                      <div
                        {...(openGallery ? { role: 'button', tabIndex: 0, onKeyDown: (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openGallery(); }
                        } } : {})}
                        onClick={openGallery}
                        title={openGallery ? t('board.viewPhotos') : undefined}
                        aria-label={openGallery ? `${t('board.viewPhotos')}: ${mp.name}` : undefined}
                        style={{
                          width: 46, height: 46, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
                          background: 'linear-gradient(135deg, #3a2a5a, #241833)', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(99,102,241,0.3)',
                          cursor: openGallery ? 'pointer' : 'default', position: 'relative',
                        }}
                      >
                        {primaryUrl ? (
                          <img src={primaryUrl}
                            alt={`${t('board.portraitAlt', { name: mp.name })}${mp.role ? ', ' + mp.role : ''}`} loading="lazy"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        ) : (
                          <span style={{ fontSize: '1.2rem', color: '#c7d2fe', fontWeight: 700 }}>{(mp.name || '?').charAt(0)}</span>
                        )}
                        {galleryUrls.length > 1 && (
                          <span style={{
                            position: 'absolute', bottom: -1, right: -1, fontSize: '0.55rem', lineHeight: 1.4,
                            background: 'rgba(99,102,241,0.9)', color: '#fff', borderRadius: '6px', padding: '0 0.25rem',
                          }}>🖼{galleryUrls.length}</span>
                        )}
                      </div>
                    );
                  })()}
                  <div style={{ color: '#a0aec0', fontSize: '0.85rem', display: 'flex', alignItems: 'baseline', gap: '0.35rem', flexWrap: 'wrap', minWidth: 0 }}>
                    <strong style={{ color: '#e2e8f0', fontSize: '1rem' }}>{mp.name}</strong>
                    {mp.id === effectivePlayerId && <span style={{ color: '#6ee7b7', fontSize: '0.75rem' }}>{t('board.youParen')}</span>}
                    {mp.id === currentPlayer?.id && <span style={{ fontSize: '0.65rem', color: '#10b981', background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '6px', padding: '0.05rem 0.4rem' }}>{t('board.turnBadge')}</span>}
                    {mp.role ? <span>— {mp.role}</span> : null}
                  </div>
                </div>

                {(() => {
                  const stats: Record<string, any> = mp.stats || {};
                  const STAT_LABELS: Record<string, string> = {
                    hp: t('board.stats.hp'), max_hp: t('board.stats.max_hp'), mp: t('board.stats.mp'), mana: t('board.stats.mana'), level: t('board.stats.level'),
                    strength: t('board.stats.strength'), dexterity: t('board.stats.dexterity'), agility: t('board.stats.agility'),
                    constitution: t('board.stats.constitution'), intelligence: t('board.stats.intelligence'), wisdom: t('board.stats.wisdom'),
                    charisma: t('board.stats.charisma'), luck: t('board.stats.luck'), armor: t('board.stats.armor'), defense: t('board.stats.defense'),
                  };
                  const STAT_ICONS: Record<string, string> = {
                    strength: '💪', dexterity: '🤸', agility: '🤸', constitution: '🛡️',
                    intelligence: '🧠', wisdom: '📖', charisma: '✨', luck: '🍀',
                    armor: '🛡️', defense: '🛡️', mana: '🔮', mp: '🔮',
                  };
                  // Merge the world's GM-defined characteristic labels + descriptions (for tooltips).
                  const STAT_DESCS: Record<string, string> = {};
                  ((currentGame as any)?.world_metadata?.characteristics || []).forEach((c: any) => {
                    if (c?.key) {
                      STAT_LABELS[c.key] = c.label || c.key;
                      if (c.description) STAT_DESCS[c.key] = c.description;
                    }
                  });
                  const label = (k: string) => STAT_LABELS[k] || k;
                  const statTitle = (k: string) => STAT_DESCS[k] ? `${label(k)} — ${STAT_DESCS[k]}` : label(k);
                  const attrKeys = Object.keys(stats).filter((k) => !['hp', 'max_hp', 'level'].includes(k));
                  const abilities: any[] = Array.isArray(mp.abilities) ? mp.abilities : [];
                  const inventory: any[] = Array.isArray(mp.inventory) ? mp.inventory : [];
                  const hp = stats.hp;
                  const maxHp = stats.max_hp;

                  // Sum stat bonuses from currently equipped items
                  const equippedBonuses: Record<string, number> = {};
                  inventory.forEach((it) => {
                    if (it && typeof it === 'object' && it.equipped && it.bonuses) {
                      Object.entries(it.bonuses).forEach(([k, v]) => {
                        equippedBonuses[k] = (equippedBonuses[k] || 0) + Number(v || 0);
                      });
                    }
                  });

                  return (
                    <>
                      {/* HP + level + XP + resources — one tight chip row */}
                      {(() => {
                        const resources: Record<string, any> = mp.resources || {};
                        const worldRes: any[] = (currentGame as any)?.world_metadata?.resources || [];
                        const resLabel = (k: string) => worldRes.find((r) => r?.key === k)?.label || k;
                        const resDesc = (k: string) => {
                          const r = worldRes.find((x) => x?.key === k);
                          return r?.description ? `${r.label || k} — ${r.description}` : t('board.resourceFallback', { label: resLabel(k) });
                        };
                        const chip = (rgb: string, color: string): React.CSSProperties => ({
                          background: `rgba(${rgb},0.12)`, border: `1px solid rgba(${rgb},0.3)`, borderRadius: '6px',
                          padding: '0.12rem 0.4rem', color, fontWeight: 600, fontSize: '0.72rem', cursor: 'help',
                        });
                        return (
                          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.45rem' }}>
                            {hp != null && <span title={t('board.hpTooltip')} style={chip('239,68,68', '#fca5a5')}>❤️ {hp}{maxHp != null ? `/${maxHp}` : ''}</span>}
                            {stats.level != null && <span title={t('board.levelTooltip')} style={chip('99,102,241', '#c7d2fe')}>⭐ {stats.level}</span>}
                            <span title={t('board.xpTooltip')} style={chip('16,185,129', '#6ee7b7')}>✨ {mp.experience_points ?? 0}</span>
                            {Object.keys(resources).map((k) => (
                              <span key={k} title={resDesc(k)} style={chip('234,179,8', '#fde68a')}>🪙 {resLabel(k)}: {resources[k]}</span>
                            ))}
                          </div>
                        );
                      })()}

                      {/* Compact single-column sections inside each player's card. */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem', alignItems: 'start' }}>
                      {/* Attributes */}
                      {attrKeys.length > 0 && (
                        <div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem' }}>
                            {attrKeys.map((k) => {
                              const base = Number(stats[k]) || 0;
                              const bonus = equippedBonuses[k] || 0;
                              return (
                                <div key={k} title={statTitle(k)} style={{ cursor: 'help', display: 'flex', justifyContent: 'space-between', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: '6px', padding: '0.15rem 0.4rem', color: '#e2e8f0', fontSize: '0.74rem' }}>
                                  <span style={{ color: '#a0aec0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{STAT_ICONS[k] || '▫️'} {label(k)}</span>
                                  <strong style={{ flexShrink: 0, marginLeft: '0.3rem' }}>
                                    {isNaN(Number(stats[k])) ? String(stats[k]) : base + bonus}
                                    {bonus ? <span style={{ color: '#6ee7b7', fontSize: '0.68rem', marginLeft: '0.2rem' }}>(+{bonus})</span> : null}
                                  </strong>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Abilities — compact icon row (hover for name/description) */}
                      {abilities.length > 0 && (
                        <div>
                          <div style={{ color: '#a0aec0', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>⚡ {t('board.abilities')}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                            {(() => {
                              const list = abilities.map((a) => {
                                const name = typeof a === 'string' ? a : a.name;
                                const desc = typeof a !== 'string' ? a.description : undefined;
                                return { name, desc, url: abilityImageUrl(name, desc, currentGame?.universe) };
                              });
                              return abilities.map((a, i) => {
                                const aName = typeof a === 'string' ? a : a.name;
                                const aDesc = typeof a !== 'string' ? a.description : undefined;
                                return (
                                  <img
                                    key={i}
                                    onClick={() => setDetail({ list, i, title: t('board.abilities') })}
                                    src={abilityImageUrl(aName, aDesc, currentGame?.universe)}
                                    alt={aName}
                                    title={`${aName}${aDesc ? ' — ' + aDesc : ''}`}
                                    loading="lazy"
                                    style={{ width: 30, height: 30, borderRadius: '7px', objectFit: 'cover', background: 'rgba(147,51,234,0.12)', border: '1px solid rgba(147,51,234,0.3)', cursor: 'pointer' }}
                                  />
                                );
                              });
                            })()}
                          </div>
                        </div>
                      )}

                      {/* Inventory — compact icon row (hover for details; ✓ = equipped) */}
                      {inventory.length > 0 && (
                        <div>
                          <div style={{ color: '#a0aec0', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>🎒 {t('board.inventory')}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                            {(() => {
                            const invList = inventory.map((item) => {
                              const name = typeof item === 'string' ? item : (item.name || item.item);
                              const bonuses = typeof item !== 'string' && item.bonuses ? item.bonuses : null;
                              const bonusText = bonuses ? Object.entries(bonuses).map(([k, v]) => `${label(k)} +${v}`).join(', ') : '';
                              const baseDesc = typeof item !== 'string' ? item.description : undefined;
                              const desc = [baseDesc, bonusText].filter(Boolean).join(' | ') || undefined;
                              return { name, desc, url: itemImageUrl(name, baseDesc, currentGame?.universe) };
                            });
                            return inventory.map((item, i) => {
                              const isEquipped = typeof item !== 'string' && item.equipped;
                              const bonuses = typeof item !== 'string' && item.bonuses ? item.bonuses : null;
                              const bonusText = bonuses
                                ? Object.entries(bonuses).map(([k, v]) => `${label(k)} +${v}`).join(', ')
                                : '';
                              const itemName = typeof item === 'string' ? item : (item.name || item.item);
                              const itemDesc = typeof item !== 'string' ? item.description : undefined;
                              const qty = typeof item !== 'string' && item.quantity ? item.quantity : null;
                              return (
                                <div
                                  key={i}
                                  onClick={() => setDetail({ list: invList, i, title: t('board.inventory') })}
                                  title={`${itemName}${itemDesc ? ' — ' + itemDesc : ''}${bonusText ? ' | ' + bonusText : ''}${isEquipped ? ' | ' + t('board.equipped') : ''}`}
                                  style={{ position: 'relative', width: 34, height: 34, borderRadius: '7px', overflow: 'hidden', flexShrink: 0, border: `1px solid ${isEquipped ? 'rgba(16,185,129,0.55)' : 'rgba(245,158,11,0.3)'}`, background: 'rgba(0,0,0,0.35)', cursor: 'pointer' }}
                                >
                                  <img
                                    src={itemImageUrl(itemName, itemDesc, currentGame?.universe)}
                                    alt={itemName}
                                    loading="lazy"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                  />
                                  {qty && <span style={{ position: 'absolute', bottom: 0, right: 0, fontSize: '0.6rem', lineHeight: 1.2, background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '0 0.2rem', borderTopLeftRadius: '4px' }}>×{qty}</span>}
                                  {isEquipped && <span style={{ position: 'absolute', top: 0, left: 1, fontSize: '0.6rem', lineHeight: 1.2, color: '#6ee7b7' }}>✓</span>}
                                </div>
                              );
                            });
                            })()}
                          </div>
                        </div>
                      )}
                      </div>
                    </>
                  );
                })()}
              </div>
              ))}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Background Music */}
      <BackgroundMusic
        musicUrl={currentLocationMusic}
        locationName={currentLocationName || undefined}
        volume={0.015}
        autoPlay={true}
      />
    </div>
  );
}
